import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { TEAL, AMBER, BORD, CARD, DARK, MUTED, DIM, TEXT, num, day } from '../stockShared';

// Euro pallets out and back (the director, 5 Nov 2026), on Euro pallet's own page.
// Each lorry's pallets leave by themselves from its invoice; what comes back is
// recorded here. "In all" stays the same unless some are bought or lost.

const box = { background: CARD, border: `1px solid ${BORD}`, borderRadius: 12, padding: '14px 16px', display: 'grid', gap: 12, marginTop: 14 };
const field = (w) => ({ width: w, boxSizing: 'border-box', background: DARK, color: TEXT, border: `1px solid ${BORD}`, borderRadius: 8, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit' });
const button = (primary) => ({ background: primary ? TEAL : 'transparent', color: primary ? '#0f172a' : TEAL, border: `1px solid ${primary ? TEAL : `${TEAL}66`}`, borderRadius: 9, padding: '8px 14px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' });
const th = (right) => ({ textAlign: right ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: MUTED, padding: '6px 10px', borderBottom: `1px solid ${BORD}`, whiteSpace: 'nowrap' });
const td = (right) => ({ textAlign: right ? 'right' : 'left', fontSize: 13, padding: '8px 10px', borderBottom: `1px solid ${BORD}`, fontVariantNumeric: 'tabular-nums' });
const todayIs = () => new Date().toISOString().slice(0, 10);

function Figure({ label, value, sub, tone = TEXT }) {
  return (
    <div style={{ background: DARK, borderRadius: 10, padding: '10px 12px', minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: MUTED }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: tone, marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: DIM, marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

export default function EuroPalletsCard({ itemId, level, manage, phone, onChanged, onRecorded }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [here, setHere] = useState('');
  const [out, setOut] = useState('');
  const [within, setWithin] = useState('7');
  const [coming, setComing] = useState(false);
  const [qty, setQty] = useState('');
  const [date, setDate] = useState(todayIs());
  const [saving, setSaving] = useState(false);

  const read = useCallback(() => api.get('/api/v1/stock/auto/euro-pallets').then(r => setData(r.data)).catch(() => setData(null)), []);
  useEffect(() => { read(); }, [read, itemId, level]);
  if (!data || !data.item || data.item.id !== Number(itemId)) return null;

  const said = (err, words) => err.response?.data?.error || words;
  async function start() {
    setSaving(true);
    try {
      const { data: d } = await api.post('/api/v1/stock/auto/euro-pallets/start', { here, with_customer: out, within });
      setData(d);
      onChanged();
      toast.success('Euro pallets are being tracked from today.');
    } catch (err) {
      toast.error(said(err, 'It could not be started just now. Try again.'));
    } finally {
      setSaving(false);
    }
  }
  async function cameBack() {
    setSaving(true);
    try {
      const { data: d } = await api.post('/api/v1/stock/auto/euro-pallets/back', { qty, date });
      setData(d.view);
      setComing(false);
      setQty('');
      onChanged();
      onRecorded({ ids: d.ids, text: `${qty} Euro pallets came back.` });
    } catch (err) {
      toast.error(said(err, 'It could not be saved just now. Your typing is still here; try again.'));
    } finally {
      setSaving(false);
    }
  }

  if (!data.started) {
    return (
      <section style={box}>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>Out and back</h2>
          <p style={{ fontSize: 13, color: DIM, margin: '4px 0 0', lineHeight: 1.5, maxWidth: 680 }}>Say where they all are today, and it starts from there. Each lorry then takes its pallets off by itself, from its invoice, and what comes back is recorded here. The bell says when some are late.</p>
        </div>
        {manage ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 12.5, color: DIM }}>Here today, empty or under goods
              <input aria-label="Here today" inputMode="numeric" style={field(190)} value={here} onChange={e => setHere(e.target.value)} placeholder={data.last_count ? `${num(data.here)} on ${day(data.last_count)}` : ''} />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 12.5, color: DIM }}>With the customer today
              <input aria-label="With the customer today" inputMode="numeric" style={field(170)} value={out} onChange={e => setOut(e.target.value)} />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 12.5, color: DIM }}>Bell after, days
              <input aria-label="Bell after, days" inputMode="numeric" style={field(90)} value={within} onChange={e => setWithin(e.target.value)} />
            </label>
            <button type="button" style={button(true)} disabled={saving} onClick={start}>{saving ? 'Starting…' : 'Start tracking'}</button>
          </div>
        ) : <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>A director starts this here, with today's figures.</p>}
      </section>
    );
  }

  const stateOf = (l) => {
    if (l.still === 0) return <span style={{ color: TEAL, fontWeight: 700 }}>All back</span>;
    if (l.late) return <span style={{ color: AMBER, fontWeight: 700 }}>{l.days} days, late</span>;
    return <span style={{ color: DIM }}>{l.days === 0 ? 'Today' : `${l.days} ${l.days === 1 ? 'day' : 'days'}`}</span>;
  };
  return (
    <section style={box}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>Out and back</h2>
        <button type="button" style={button(true)} onClick={() => setComing(c => !c)} disabled={data.with_customer <= 0}>Came back</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 10 }}>
        <Figure label="Here" value={num(data.here)} sub={data.last_count ? `counted ${day(data.last_count)}` : null} />
        <Figure label="Under goods" value={num(data.under_goods)} sub="packed, waiting for a lorry" />
        <Figure label="With the customer" value={num(data.with_customer)} tone={data.lorries.some(l => l.late) ? AMBER : TEXT} sub={data.last_back ? `last back ${day(data.last_back)}` : 'none back yet'} />
        <Figure label="In all" value={num(data.in_all)} sub="stays the same unless bought or lost" />
      </div>
      {coming && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', background: DARK, borderRadius: 10, padding: '10px 12px' }}>
          <label style={{ display: 'grid', gap: 4, fontSize: 12.5, color: DIM }}>How many came back
            <input aria-label="How many came back" inputMode="numeric" autoFocus style={field(130)} value={qty} onChange={e => setQty(e.target.value)} placeholder={`up to ${num(data.with_customer)}`} />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 12.5, color: DIM }}>On
            <input aria-label="Came back on" type="date" style={field(150)} value={date} max={todayIs()} onChange={e => setDate(e.target.value)} />
          </label>
          <button type="button" style={button(true)} disabled={saving} onClick={cameBack}>{saving ? 'Saving…' : 'Save'}</button>
          <button type="button" style={button(false)} onClick={() => setComing(false)}>Cancel</button>
        </div>
      )}
      {data.lorries.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr><th style={th()}>Lorry</th><th style={th()}>Went</th><th style={th(true)}>Out</th><th style={th(true)}>Back</th><th style={th(true)}>Still out</th><th style={th()}></th></tr></thead>
            <tbody>
              {data.lorries.map(l => (
                <tr key={l.id}>
                  <td style={td()}>{l.label.charAt(0).toUpperCase() + l.label.slice(1)}</td>
                  <td style={{ ...td(), whiteSpace: 'nowrap' }}>{day(l.date)}</td>
                  <td style={td(true)}>{num(l.out)}</td>
                  <td style={td(true)}>{num(l.back)}</td>
                  <td style={{ ...td(true), fontWeight: 700, color: l.late ? AMBER : TEXT }}>{num(l.still)}</td>
                  <td style={td()}>{stateOf(l)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(data.no_figure || []).length > 0 && <p style={{ fontSize: 13, color: AMBER, margin: 0 }}>{data.no_figure.join(', ')}: the invoice has no number of Euro pallets, so its lorry is not counted here. Record those that left with Used on this page.</p>}
      <p style={{ fontSize: 12.5, color: DIM, margin: 0, lineHeight: 1.5 }}>Each lorry's pallets leave by themselves, from its invoice. What comes back goes against the oldest lorry first. The bell says when some are still out after {data.within_days} days.{data.lorries.length === 0 ? ' The first lorry shows here when its invoice is issued.' : ''}</p>
    </section>
  );
}
